"use client";

// Core
import { useRouter } from "next/navigation";
import { useStore } from "@/hooks/use-store";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import Image from "next/image";
import cx from "classnames";

// Definitions
import { GameType } from "@/domains/games/types";

// Components
import IconSprite from "@/components/UI/IconSprite";
import GameMobileViewModal from "src/domains/games/components/game-mobile-view-modal";

// Utils
import styles from "./GameCard.module.scss";
import { routes } from "@/definitions/book";
import { PUBLIC_IMAGE_URL } from "@/network/http-config";
import { default as ImgBlur } from "@/images/img-blur.webp";
import { useMediaQuery } from "@mui/material";

export type GameCardProps = {
  item: GameType;
  label?: string;
  inSlide?: boolean;
};

export default function GameCard({ label, item, inSlide }: GameCardProps) {
  const demoBtn = useTranslations("Buttons");
  const { modal, modalSheet } = useStore();
  const isTabletOrMobile = useMediaQuery("only screen and (max-width: 1024px)");
  const router = useRouter();

  const handlerClickCard = () => {
    if (isTabletOrMobile) {
      modalSheet.closeMenu();

      setTimeout(() => {
        modal.showModal(<GameMobileViewModal item={item} onClose={modal.closeModal} />);
      }, 150);

      return;
    }

    router.push(`${routes.game}/${item.slug}`);
    modal.closeModal();
  };

  return (
    <article className={cx(styles.card, { ...(inSlide && { [styles.in_slide]: inSlide }) })}>
      {item.image.endsWith(".gif") ? (
        <img
          className={styles.img}
          src={`${PUBLIC_IMAGE_URL}${item.image}`}
          alt={item.name}
          width={186}
          height={232}
          loading="lazy"
        />
      ) : (
        <Image
          className={styles.img}
          src={`${PUBLIC_IMAGE_URL}${item.image}`}
          alt={item.name}
          width={186}
          height={232}
          loading="lazy"
          blurDataURL={ImgBlur.src}
          placeholder="blur"
        />
      )}

      {label && <span className={styles.label}>{label}</span>}

      <button className={styles.overlay_link} onClick={handlerClickCard}></button>

      <div className={styles.overlay}>
        <div className={styles.body}>
          <h1 className={styles.title}>{item.name}</h1>

          <Link className={styles.play_btn} href={`${routes.game}/${item.slug}`} aria-label="Play">
            <IconSprite name="play" className={styles.play_btn__icon} />
          </Link>

          {item.provider.demo && (
            <Link className={styles.demo_btn} href={`${routes.game}/${item.slug}?demo=true`}>
              {demoBtn("demo")}
            </Link>
          )}
        </div>
        <div className={styles.footer}>
          {item.slug && (
            <Link className={styles.footer_link} href={`${routes.provider}/${item.provider.slug}`}>
              {item.provider.image && (
                <Image
                  className={styles.provider_logo}
                  src={`${PUBLIC_IMAGE_URL}${item.provider.image}`}
                  alt={item.provider.name}
                  width={33}
                  height={33}
                  loading="lazy"
                />
              )}
            </Link>
          )}
        </div>
      </div>
    </article>
  );
}
